<script lang="ts" setup>
import type { TabEvents, NavigationTab, NavigationTabPayload } from '@/types'

interface Props {
  tab: NavigationTab
  tabName: string
  isLastItem: boolean
  index: number
  isSelected: boolean
  tabIndex: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'change-tab-state', payload: NavigationTabPayload): void
}>()

const localePath = useLocalePath()

const isDirectLink = computed(
  () => Boolean(props.tab.to) && props.tab.sections.length === 0,
)

const handleKeyDown = (event: KeyboardEvent) => {
  switch (event.code) {
    case 'ArrowLeft':
    case 'ArrowRight':
      focusNextElement(event)
      break
    case 'Escape':
      handleTabChange(event, { type: 'RESET' })
      break
    case 'Enter':
      if (!isDirectLink.value) {
        handleTabChange(event, { type: 'TOGGLE' })
      }
      break
    case 'Tab':
      if (event.shiftKey) {
        handleTabChange(event, { type: 'TOGGLE' })
      }
      break
  }
}

const focusNextElement = (event: KeyboardEvent) => {
  const eventType = event.code === 'ArrowRight' ? 'OPEN_NEXT' : 'OPEN_PREVIOUS'

  handleTabChange(event, { type: eventType })
}

const handleTabChange = (e: Event, event: TabEvents) => {
  const offset = calculateOffset(e)

  const payload = {
    offset,
    event,
  }

  emit('change-tab-state', payload)
}

const calculateOffset = (event: Event) => {
  const target = event.currentTarget as HTMLElement
  const { width } = target.getBoundingClientRect()
  return `${target.offsetLeft + width / 2}px`
}
</script>

<template>
  <li
    role="presentation"
    :class="[
      'navigation-header-item',
      isSelected && 'navigation-header-item--active',
    ]"
  >
    <NuxtLink
      v-if="isDirectLink"
      :to="localePath(tab.to!)"
      :data-index="index"
      :tabindex="tabIndex"
      class="navigation-header-item__tab-button"
      @mouseenter="handleTabChange($event, { type: 'RESET' })"
      @focus="handleTabChange($event, { type: 'RESET' })"
      @keydown="handleKeyDown"
    >
      <span>{{ $t(`common.navigation.tab_names.${tab.tabName}`) }}</span>
    </NuxtLink>
    <button
      v-else
      role="tab"
      type="button"
      :data-index="index"
      :tabindex="tabIndex"
      aria-controls="navigation-tab-panel"
      class="navigation-header-item__tab-button"
      :aria-expanded="isSelected"
      :aria-selected="isSelected"
      @mouseenter="handleTabChange($event, { type: 'OPEN', index })"
      @focus="handleTabChange($event, { type: 'OPEN', index })"
      @keydown="handleKeyDown"
    >
      <span>{{ $t(`common.navigation.tab_names.${tab.tabName}`) }}</span>
      <span class="navigation-header-item__icon" aria-hidden="true">
        <BaseIconSvg name="12-chevron-down" width="12" height="12" />
      </span>
    </button>
  </li>
</template>

<style lang="scss" scoped>
.navigation-header-item {
  $this: &;

  --animation-duration: 200ms;

  display: contents;

  &__tab-button {
    @include paragraph-s;

    position: relative;
    padding: 8px 16px;
    display: flex;
    gap: 6px;
    align-items: center;
    color: inherit;
    letter-spacing: inherit;
    text-decoration: none;
    border-radius: 30px;
    transition: background-color 0.2s ease;

    &:focus-visible {
      outline: none;
      border: 1px solid var(--cobalt-600);
    }

    &:hover {
      color: inherit;
      text-decoration: none;
      background-color: var(--navbar-item-hover-bg, var(--paper-100));
    }

    &:active {
      background-color: var(--navbar-item-active-bg, var(--paper-200));
    }
  }

  &__icon {
    display: flex;
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: inherit;
  }

  &--active {
    #{$this}__tab-button {
      background-color: var(--navbar-item-hover-bg, var(--paper-100));
    }

    #{$this}__icon {
      transform: rotate(180deg);
    }
  }
}
</style>
